import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { yardApi } from '@/api/services/yard.api';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

export default function YardJobKitsNew() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    wellName: '',
    jobType: '',
    requiredDate: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Please enter a kit name');
      return;
    }

    try {
      setSaving(true);
      await yardApi.jobKits.create({
        name: formData.name,
        description: formData.description || undefined,
        wellName: formData.wellName || undefined,
        jobType: formData.jobType || undefined,
        requiredDate: formData.requiredDate || undefined,
      });
      toast.success('Job kit created successfully');
      navigate('/yard/job-kits');
    } catch (error) {
      toast.error('Failed to create job kit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="New Job Kit"
        description="Job, contents, and required date needed to raise a kit for assembly."
        actions={
          <Button variant="outline" onClick={() => navigate('/yard/job-kits')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Job Kits
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Kit Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Kit Details</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="name">Kit Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="BHA Kit - Well A1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Kit description and contents..."
                rows={3}
              />
            </div>
          </div>

          {/* Job Information */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Job Information</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="wellName">Well Name</Label>
                <Input
                  id="wellName"
                  value={formData.wellName}
                  onChange={(e) => setFormData({ ...formData, wellName: e.target.value })}
                  placeholder="Well A1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jobType">Job Type</Label>
                <Input
                  id="jobType"
                  value={formData.jobType}
                  onChange={(e) => setFormData({ ...formData, jobType: e.target.value })}
                  placeholder="Drilling, Completion, etc."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="requiredDate">Required Date</Label>
              <Input
                id="requiredDate"
                type="date"
                value={formData.requiredDate}
                onChange={(e) => setFormData({ ...formData, requiredDate: e.target.value })}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/yard/job-kits')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Job Kit'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
